/**
 * The figures of the Invoice Payment form, in whole cents (no floating-point drift) and by the ONE rule the server uses (core/payment-settlement.js):
 *
 *   Invoice Amount   = the invoice's total
 *   Prior Payments   = the other payments of the invoice that are not voided (pending and posted both count) - never this payment itself
 *   This Payment     = the amount typed, unless this payment is voided (a voided payment is not money paid)
 *   Balance Due      = Invoice Amount - Prior Payments - This Payment, never below zero
 *   Left to pay      = Invoice Amount - Prior Payments: the most this payment may be ("Paid in full" puts exactly that in the Amount)
 *
 * The invoice's status is not computed here: the server derives it from the payments that are saved.
 */
export interface PaymentRow {
  id?: string;
  amount?: number | string | null;
  status?: string | null;
}

export const toCents = (value: number | string | null | undefined): number => Math.round(Number(value || 0) * 100);
export const fromCents = (cents: number): number => Math.round(cents) / 100;

/** A voided payment is not money paid. */
export const countsAsPaid = (payment: Pick<PaymentRow, 'status'>): boolean => payment.status !== 'voided';

export function priorPaymentsCents(payments: PaymentRow[], editingId?: string | null): number {
  return payments
    .filter((payment) => (!editingId || payment.id !== editingId) && countsAsPaid(payment))
    .reduce((sum, payment) => sum + toCents(payment.amount), 0);
}

export interface PaymentFigures {
  invoiceCents: number;
  priorCents: number;
  thisCents: number;
  /** Invoice Amount - Prior Payments (>= 0): what is left to pay before this payment */
  leftToPayCents: number;
  /** Balance Due after this payment (>= 0) */
  balanceCents: number;
  /** this payment (with the prior ones) would take the invoice above its total */
  overpaid: boolean;
}

export function paymentFigures(input: { totalAmount: number | string | null | undefined; payments: PaymentRow[]; editingId?: string | null; amount: number | string | null | undefined; status?: string | null }): PaymentFigures {
  const invoiceCents = toCents(input.totalAmount);
  const priorCents = priorPaymentsCents(input.payments, input.editingId);
  const thisCents = countsAsPaid({ status: input.status }) ? toCents(input.amount) : 0;
  const rest = invoiceCents - priorCents - thisCents;
  return { invoiceCents, priorCents, thisCents, leftToPayCents: Math.max(0, invoiceCents - priorCents), balanceCents: Math.max(0, rest), overpaid: rest < 0 };
}

/** The same figures, as the dollars the form shows. */
export const dollars = (cents: number): number => fromCents(cents);
